/**
 * Swipe-down (or swipe-toward-edge, in a landscape side drawer) to dismiss a
 * bottom-sheet/drawer — brief addendum §4. Bound to a small dedicated handle
 * element, not the whole sheet, so it never competes with the sheet's own
 * internal scroll containers or, in the Tessellation panel, native
 * `<input type="range">` sliders.
 *
 * Deliberately minimal: no spring-back/inertia math of its own. Clearing the
 * inline `dragStyle` on release hands off to the drawer's existing CSS
 * `transition: transform 0.22s ease` (App.vue), which animates from the last
 * dragged position to whichever class-driven end-state (open, or closed if
 * `onDismiss` just fired) is current — the same transition already used for
 * the tap-to-toggle open/close.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';

export interface SwipeDismissOptions {
  /** 'y' for a portrait bottom sheet, 'x' for a landscape side drawer. */
  axis: () => 'x' | 'y';
  /** Which drag direction counts as "toward dismissed" — a bottom sheet is
   *  always +1 (down); a side drawer matches whichever direction its own
   *  closed-state transform already uses. */
  sign: () => 1 | -1;
  onDismiss: () => void;
}

export interface SwipeDismiss {
  dragStyle: ComputedRef<Record<string, string>>;
  onPointerDown(e: PointerEvent): void;
  onPointerMove(e: PointerEvent): void;
  onPointerUp(e: PointerEvent): void;
}

/** Drag past this fraction of the drawer's own size to commit the dismiss;
 *  short of it springs back. */
const DISMISS_FRACTION = 0.3;

export function useSwipeDismiss(el: Ref<HTMLElement | null>, opts: SwipeDismissOptions): SwipeDismiss {
  const dragPx = ref(0);
  const dragging = ref(false);
  let startClient = 0;
  let dim = 1;

  function onPointerDown(e: PointerEvent): void {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragging.value = true;
    startClient = opts.axis() === 'y' ? e.clientY : e.clientX;
    dim = (opts.axis() === 'y' ? el.value?.offsetHeight : el.value?.offsetWidth) || 1;
  }

  function onPointerMove(e: PointerEvent): void {
    if (!dragging.value) return;
    const client = opts.axis() === 'y' ? e.clientY : e.clientX;
    const delta = (client - startClient) * opts.sign();
    dragPx.value = Math.max(0, Math.min(dim, delta));
  }

  function onPointerUp(): void {
    if (!dragging.value) return;
    dragging.value = false;
    const commit = dragPx.value > dim * DISMISS_FRACTION;
    dragPx.value = 0;
    if (commit) opts.onDismiss();
  }

  const dragStyle = computed<Record<string, string>>(() => {
    const style: Record<string, string> = {};
    if (!dragging.value || dragPx.value === 0) return style;
    const px = opts.sign() * dragPx.value;
    const fn = opts.axis() === 'y' ? 'translateY' : 'translateX';
    style.transform = `${fn}(${px}px)`;
    style.transition = 'none';
    return style;
  });

  return { dragStyle, onPointerDown, onPointerMove, onPointerUp };
}
